import Vibrant from 'node-vibrant';
import { error } from './log';
import { sortBy } from './array';

import {
  DEFAULT_ELEMENT_COLOR_CLASS,
  ELEMENT_COLOR_CLASSES,
} from '../constants';

const { Util: colorUtil } = Vibrant;

/**
 * Given a hex string like #rgb or #rrggbb, returns a number in [0,1], where 0
 * is darkest and 1 is brightest.
 */
export function brightness(hexColorString = '') {
  // FIXME: We need a color model distinct from hex strings.

  if (typeof hexColorString !== 'string' || hexColorString[0] !== '#') {
    error(`Invalid hex color string: ${hexColorString}`);
    return 0;
  }

  // Extract RGB components from the hex color string
  const hexNumeralString = hexColorString.substring(1);
  let redString;
  let greenString;
  let blueString;

  switch (hexNumeralString.length) {
    case 3:
      redString = hexNumeralString[0] + hexNumeralString[0];
      greenString = hexNumeralString[1] + hexNumeralString[1];
      blueString = hexNumeralString[2] + hexNumeralString[2];
      break;
    case 6:
    case 8:
      redString = hexNumeralString.substr(0, 2);
      greenString = hexNumeralString.substr(2, 2);
      blueString = hexNumeralString.substr(4, 2);
      break;
    default:
      error(`Invalid hex color string: ${hexColorString}`);
      return 0;
  }

  // Parse the RGB components into numbers
  const red = parseInt(redString, 16);
  const green = parseInt(greenString, 16);
  const blue = parseInt(blueString, 16);

  // https://www.w3.org/WAI/ER/WD-AERT/#color-contrast
  return (red * 299 + green * 587 + blue * 114) / 255000;
}

export const isColorBright = (color) => {
  return brightness(color) > 0.5;
};

export function complementaryRgbColor(r, g, b) {
  return colorUtil.rgbToHex(255 - r, 255 - g, 255 - b);
}

// determine whether white or black looks better for a color
// http://stackoverflow.com/a/3943023/112731
export function determineWhiteOrBlackOnColor(r, g, b) {
  return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? '#000000' : '#ffffff';
}

export const colorInformation = (url) => {
  if (url) {
    const image = new Vibrant(url);
    return new Promise((resolve) => {
      image.getPalette((err, palettes) => {
        if (err || !palettes) return resolve({});

        const foundPalettes = [];
        const avgColor = { r: 0, g: 0, b: 0 };
        let totalPopulation = 0;
        Object.values(palettes).forEach((palette) => {
          if (palette) {
            const rgb = palette.getRgb();
            const r = rgb[0];
            const g = rgb[1];
            const b = rgb[2];

            const population = palette.getPopulation();
            totalPopulation += population;

            foundPalettes.push({
              color: palette.getHex(),
              complementary: complementaryRgbColor(r, g, b),
              population,
              rgb: { r, g, b },
            });
          }
        });

        const sortedColors = sortBy(
          foundPalettes,
          'population',
          (a, b) => b - a,
        );
        const colors = { colors: [], complementary: [] };
        sortedColors.forEach(({ color, complementary, rgb, population }) => {
          avgColor.r += rgb.r * (population / totalPopulation);
          avgColor.g += rgb.g * (population / totalPopulation);
          avgColor.b += rgb.b * (population / totalPopulation);

          colors.colors.push(color);
          colors.complementary.push(complementary);
        });

        const bwColor = determineWhiteOrBlackOnColor(
          avgColor.r,
          avgColor.g,
          avgColor.b,
        );
        const averageColor = colorUtil.rgbToHex(
          Math.round(avgColor.r),
          Math.round(avgColor.g),
          Math.round(avgColor.b),
        );

        colors.colors.unshift(bwColor);
        colors.complementary.unshift(averageColor);

        return resolve(colors);
      });
    });
  } else {
    return {};
  }
};

export const getColorFromType = (contentType) => {
  return ELEMENT_COLOR_CLASSES[contentType] || DEFAULT_ELEMENT_COLOR_CLASS;
};
